import React from "react";
import { useSearchParams } from "react-router";

import { type CrudFilter, type CrudSort, HttpError } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { DataTableActionBar } from "@/components/refine-ui/data-table/data-table-action-bar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FilterSelect } from "@/pages/areas/components/filter-select";
import { ReorderPartsDialog } from "@/pages/areas/components/reorder-parts-dialog";
import { useAreaParts } from "@/pages/areas/hooks/use-area-parts";
import { useAreaColumns } from "@/pages/areas/hooks/use-columns";
import { useReloadAreas } from "@/pages/areas/hooks/use-reload-areas";
import { selectAreaRows } from "@/pages/areas/lib/area-hits";
import type { AreaListItem } from "@/types/area";

/**
 * Typesense filters for the list. Every entry is emitted on every render, `value: undefined`
 * when cleared: omitting a cleared entry leaves a stale copy in Refine's internal filter state
 * (see `buildItemsFilters` in the tina items list), and `transformFilter` drops the blank ones.
 * "No part" means an area with no parent, so it also pins the kind to `area`.
 */
export const buildAreaFilters = (f: {
  cityKey?: string;
  parentId?: string;
  kind?: string;
  origin?: string;
  zeroVenues: boolean;
  noPart: boolean;
}): CrudFilter[] => [
  { field: "cityKey", operator: "eq", value: f.cityKey },
  { field: "parentId", operator: "eq", value: f.parentId },
  { field: "kind", operator: "eq", value: f.noPart ? "area" : f.kind },
  { field: "origin", operator: "eq", value: f.origin },
  { field: "venueCount", operator: "eq", value: f.zeroVenues ? 0 : undefined },
  { field: "hasParent", operator: "eq", value: f.noPart ? false : undefined },
];

/** Cities alphabetically, the biggest areas of a city first. */
const DEFAULT_SORT: CrudSort[] = [
  { field: "city", order: "asc" },
  { field: "venueCount", order: "desc" },
];

export const AreasListTab = ({ cityKey }: { cityKey: string | undefined }) => {
  const reload = useReloadAreas();
  const { parts: cityParts, partNames } = useAreaParts(cityKey);
  const columns = useAreaColumns(partNames);
  // The search box writes `?query=`; Refine folds it into the list query's meta.
  const [searchParams] = useSearchParams();
  const q = searchParams.get("query") ?? undefined;

  const [parentId, setParentId] = React.useState<string>();
  const [kind, setKind] = React.useState<string>();
  const [origin, setOrigin] = React.useState<string>();
  const [zeroVenues, setZeroVenues] = React.useState(false);
  const [noPart, setNoPart] = React.useState(false);
  const [reorderOpen, setReorderOpen] = React.useState(false);

  // A part belongs to one city: a new city starts without the old one's part.
  React.useEffect(() => setParentId(undefined), [cityKey]);

  const table = useTable<AreaListItem, HttpError>({
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: { columnPinning: { right: ["actions"] } },
    refineCoreProps: {
      resource: "areas",
      dataProviderName: "areas",
      meta: { defaultQueryBy: "name,aliases" },
      queryOptions: { select: selectAreaRows },
      sorters: { initial: DEFAULT_SORT },
      filters: {
        permanent: buildAreaFilters({
          cityKey,
          parentId,
          kind,
          origin,
          zeroVenues,
          noPart,
        }),
      },
      pagination: { pageSize: 25 },
    },
  });

  // A filter change shrinks the set, so a click never strands the table on a page past its end.
  const filterSignature = JSON.stringify([
    q,
    cityKey,
    parentId,
    kind,
    origin,
    zeroVenues,
    noPart,
  ]);
  const setCurrentPage = table.refineCore.setCurrentPage;
  const previousFilters = React.useRef(filterSignature);
  React.useEffect(() => {
    if (previousFilters.current === filterSignature) return;
    previousFilters.current = filterSignature;
    setCurrentPage(1);
  }, [filterSignature, setCurrentPage]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Part"
          value={parentId}
          onChange={setParentId}
          disabled={!cityKey}
          options={cityParts.map((p) => ({ value: p.id, label: p.name }))}
        />
        <FilterSelect
          label="Kind"
          value={kind}
          onChange={setKind}
          options={[
            { value: "area", label: "Area" },
            { value: "part", label: "Part" },
          ]}
        />
        <FilterSelect
          label="Origin"
          value={origin}
          onChange={setOrigin}
          options={[
            { value: "cleanup", label: "cleanup" },
            { value: "auto", label: "auto" },
            { value: "curator", label: "curator" },
          ]}
        />
        <div className="flex items-center gap-1">
          <Checkbox
            id="areas-zero-venues"
            checked={zeroVenues}
            onCheckedChange={(c) => setZeroVenues(c === true)}
          />
          <Label htmlFor="areas-zero-venues">0 venues</Label>
        </div>
        <div className="flex items-center gap-1">
          <Checkbox
            id="areas-no-part"
            checked={noPart}
            onCheckedChange={(c) => setNoPart(c === true)}
          />
          <Label htmlFor="areas-no-part">No part</Label>
        </div>
        {cityParts.length >= 2 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReorderOpen(true)}
          >
            Reorder parts
          </Button>
        )}
      </div>
      <DataTableActionBar resource="areas" table={table} />
      <DataTable table={table} />
      <ReorderPartsDialog
        parts={cityParts}
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        onSaved={reload}
      />
    </>
  );
};
